import React, { useState, useEffect } from 'react';
import { X, Scissors, CheckCircle, AlertCircle, Loader2, Download } from 'lucide-react';

const BackgroundRemovalSnackbar = ({ 
  jobs = [], 
  onComplete, 
  onCancel, 
  onDownload 
}) => {
  const [visibleJobs, setVisibleJobs] = useState([]);

  useEffect(() => {
    // Filter jobs to show only active or recently completed ones
    const activeJobs = jobs.filter(job => 
      ['starting', 'processing', 'completed', 'failed'].includes(job.status)
    );
    setVisibleJobs(activeJobs);
  }, [jobs]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'starting':
      case 'processing':
        return <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />;
      default:
        return <Scissors className="w-4 h-4 text-purple-500" />;
    }
  };

  const getStatusMessage = (status, progress = 0) => {
    switch (status) {
      case 'starting':
        return '🚀 Starting background removal...';
      case 'processing':
        return `🎨 Removing background... ${Math.round(progress)}%`;
      case 'completed':
        return '✨ Background removed successfully!';
      case 'failed':
        return '❌ Background removal failed';
      default:
        return 'Processing...';
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'processing':
        return 'bg-purple-500';
      default:
        return 'bg-gray-400';
    }
  };

  const handleClose = (jobId) => {
    setVisibleJobs(prev => prev.filter(job => job.id !== jobId));
  };

  const handleCancel = (jobId) => {
    if (onCancel) {
      onCancel(jobId);
    }
    handleClose(jobId);
  };

  const handleComplete = (job) => {
    if (onComplete && job.new_image_url) {
      onComplete(job.new_image_url, job.filename);
    }
    handleClose(job.id);
  };

  const handleDownload = (job) => {
    if (onDownload && job.new_image_url) {
      onDownload(job.new_image_url, job.filename);
    }
  };

  if (visibleJobs.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm space-y-3">
      {visibleJobs.map((job) => (
        <div
          key={job.id}
          className="bg-purple-50 border border-purple-200 rounded-lg shadow-lg p-4 transition-all duration-300 ease-in-out transform translate-x-0 opacity-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-medium text-purple-900">
                Background Removal
              </span>
              {getStatusIcon(job.status)}
            </div>
            <button
              onClick={() => handleClose(job.id)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Message */}
          <div className="text-sm text-gray-700 mb-3">
            {job.status === 'failed' ? (job.error || 'Background removal failed') : getStatusMessage(job.status, job.progress)}
          </div>

          {/* Progress Bar */}
          {(job.status === 'processing' || job.status === 'starting') && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span className="font-mono bg-white px-2 py-1 rounded">
                  {Math.round(job.progress || 0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ease-out ${getProgressBarColor(job.status)}`}
                  style={{ width: `${Math.max(job.progress || 0, 5)}%` }}
                >
                  <div className="h-full w-full rounded-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                </div>
              </div>
            </div>
          )}

          {/* Filename */}
          {job.filename && (
            <div className="text-xs text-gray-500 mb-2 bg-white px-2 py-1 rounded">
              📁 {job.filename}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {(job.status === 'starting' || job.status === 'processing') && (
              <button
                onClick={() => handleCancel(job.id)}
                className="px-3 py-1 text-xs bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
            
            {job.status === 'completed' && job.new_image_url && (
              <>
                <button
                  onClick={() => handleComplete(job)}
                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                >
                  Apply Image
                </button>
                <button
                  onClick={() => handleDownload(job)}
                  className="px-3 py-1 text-xs bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Download
                </button>
              </>
            )}

            {job.status === 'failed' && (
              <button
                onClick={() => handleClose(job.id)}
                className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BackgroundRemovalSnackbar;
